import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../utils/api';
import { X } from 'lucide-react';
import BackButton from '../../components/BackButton';

const ClientNotes = () => {
  const { id } = useParams();
  const token = localStorage.getItem('token');
  const [notes, setNotes] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/notes/client/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotes(res.data);
      } catch (err) {
         toast.error('Error loading notes');
      
      }
    };

    fetchNotes();
  }, [id, token]);

  const toggleSelect = (noteId) => {
    setSelected((prev) =>
      prev.includes(noteId) ? prev.filter((id) => id !== noteId) : [...prev, noteId]
    );
  };

  const exportNotes = async (mode = 'selected') => {
    try {
      const payload = mode === 'all' ? { clientId: id } : { noteIds: selected };

      const res = await axios.post(`${API_BASE_URL}/api/pdf/export`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ShiftNotes_${mode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error('Failed to export PDF');
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      setSelected((prev) => prev.filter((id) => id !== noteId));
      toast.success("Deleted Successfully!");
    } catch (err) {
      toast.error('Failed to delete note')
    }
  };

  return (
    <div className="min-h-screen bg-[#0E0A1F] text-white p-6">
      <BackButton/>
      <div className="max-w-3xl mx-auto space-y-6">
        <h2 className="text-2xl font-bold mb-2">Shift Notes</h2>

        {notes.length > 0 && (
          <div className="flex justify-end gap-4 mb-4">
            <button
              onClick={() => exportNotes('selected')}
              disabled={selected.length === 0}
              className={`px-4 py-2 rounded ${
                selected.length === 0
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-[#8F4EFF] hover:bg-purple-700'
              }`}
            >
              Export Selected ({selected.length})
            </button>
            <button
              onClick={() => exportNotes('all')}
              className="bg-[#8F4EFF] hover:bg-purple-700 px-4 py-2 rounded"
            >
              Export All
            </button>
          </div>
        )}

        {notes.length === 0 ? (
          <p className="text-gray-400">No notes available.</p>
        ) : (
          notes.map((note) => {
            const client = note.Shift?.Client;
            return (
              <div
                key={note.id}
                className="bg-[#1C1235] p-4 rounded-xl shadow space-y-3 relative"
              >
                <div className="absolute top-4 right-4 flex gap-2 items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={selected.includes(note.id)}
                    onChange={() => toggleSelect(note.id)}
                  />
                  <button
                    onClick={() => deleteNote(note.id)}
                    className="text-red-400 hover:text-red-600"
                    title="Delete note"
                  >
                    <X size={16} />
                  </button>
                </div>

                <p className="text-sm text-gray-400">
                  <b>Client:</b> {client?.name || 'N/A'}
                </p>
                <p className="text-sm text-gray-400">
                  <b>Address:</b> {client?.address || 'N/A'}
                </p>
                <p className="text-sm text-gray-400">
                  <b>Shift:</b> {note.Shift?.title || 'Untitled'} | {note.Shift?.date} |{' '}
                  {note.Shift?.startTime} - {note.Shift?.endTime}
                </p>

                <div className="flex gap-4 items-start">
                  <img
                    src={note.photo ? `${API_BASE_URL}${note.photo}` : '/default-avatar.png'}
                    alt="Note"
                    className="w-24 h-24 border border-gray-600 rounded object-cover"
                  />
                  <p className="text-white whitespace-pre-wrap flex-1">
                    {note.content}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ClientNotes;
